import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../components/FormField.js';
import { TabStrip } from '../../components/TabStrip.js';
import { describeError } from '../../errors.js';
import { useFetchOnMount } from '../../hooks/useFetchOnMount.js';
import type { AdminHttpClient } from '../../http.js';
import { providerTestErrorMessage } from '../../shared/provider-test-error.js';
import type {
  PlatformProviderKeyProvider,
  PlatformProviderModelDefinition,
  ProviderKeySummary,
  ToastMessage,
} from '../../types.js';
import {
  DEFAULT_LITELLM_BASE_URL,
  PROVIDERS,
  baseUrlsForProvider,
  defaultBaseUrlFor,
  isSharedLiteLLMBaseUrlSnapshot,
} from './base-urls.js';
import { bulkDeployBuckets } from './bulk-deploy-buckets.js';

/** Provider key registration + one selected-provider work panel with connection tests.
 *  The raw apiKey only ever lives in this component's transient state — it is
 *  cleared on successful registration and never copied into list/assignment
 *  state. */
export function ProviderKeysTab({
  http,
  tenantId,
  keys,
  busy,
  setBusy,
  onToast,
  onRegistered,
  isActive,
  onOpenAssignment,
  onOpenManualProviderModels,
}: {
  http: AdminHttpClient;
  tenantId: string;
  keys: ProviderKeySummary[];
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onToast: (toast: ToastMessage) => void;
  /** Container re-pulls keys + assignment for the tenant. */
  onRegistered: () => Promise<void>;
  isActive: boolean;
  onOpenAssignment?: () => void;
  onOpenManualProviderModels?: () => void;
}) {
  const { t } = useTranslation();
  const translate = (key: string, options?: Record<string, unknown>): string =>
    String(options ? t(key, options) : t(key));
  const [provider, setProvider] = useState<PlatformProviderKeyProvider>('openai');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState(defaultBaseUrlFor('openai'));
  const [litellmBaseUrl, setLiteLLMBaseUrl] = useState(DEFAULT_LITELLM_BASE_URL);
  const [testingProvider, setTestingProvider] = useState<PlatformProviderKeyProvider | null>(null);
  const [testingLiteLLM, setTestingLiteLLM] = useState<PlatformProviderKeyProvider | null>(null);
  const [bulkDeployingProvider, setBulkDeployingProvider] =
    useState<PlatformProviderKeyProvider | null>(null);
  const [definitionPage, setDefinitionPage] = useState<{
    provider: PlatformProviderKeyProvider | null;
    items: PlatformProviderModelDefinition[];
    total: number;
  }>({ provider: null, items: [], total: 0 });

  const fetchDefinitionsFor = useCallback(
    (targetProvider: PlatformProviderKeyProvider) =>
      http.listPlatformProviderModels({
        provider: targetProvider,
        status: 'active',
        limit: 100,
      }),
    [http]
  );
  const fetchDefinitions = useCallback(
    () => fetchDefinitionsFor(provider),
    [fetchDefinitionsFor, provider]
  );
  const onDefinitionsLoaded = useCallback(
    (page: { items: PlatformProviderModelDefinition[]; total: number }) => {
      setDefinitionPage({ provider, items: page.items, total: page.total });
    },
    [provider]
  );
  const onDefinitionsError = useCallback(
    (error: unknown) =>
      onToast({
        tone: 'error',
        message: describeError(error, (key, options) =>
          String(options ? t(key, options) : t(key))
        ),
      }),
    [onToast, t]
  );

  useFetchOnMount({
    enabled: isActive && Boolean(tenantId.trim()),
    fetcher: fetchDefinitions,
    onData: onDefinitionsLoaded,
    onError: onDefinitionsError,
  });

  useEffect(() => {
    const nextUrls = baseUrlsForProvider(provider, keys);
    setBaseUrl(nextUrls.providerBaseUrl);
    setLiteLLMBaseUrl(nextUrls.litellmBaseUrl);
  }, [keys, provider]);

  useEffect(() => {
    setApiKey('');
  }, [tenantId]);

  function changeProvider(next: PlatformProviderKeyProvider) {
    setProvider(next);
    setApiKey('');
    const nextUrls = baseUrlsForProvider(next, keys);
    setBaseUrl(nextUrls.providerBaseUrl);
    setLiteLLMBaseUrl(nextUrls.litellmBaseUrl);
  }

  function customLiteLLMBaseUrl(): string | undefined {
    const trimmed = litellmBaseUrl.trim();
    return trimmed && !isSharedLiteLLMBaseUrlSnapshot(trimmed) ? trimmed : undefined;
  }

  async function register(event: FormEvent) {
    event.preventDefault();
    if (!tenantId.trim() || !apiKey.trim()) return;
    setBusy(true);
    try {
      const nextBaseUrl = baseUrl.trim();
      const nextLiteLLMBaseUrl = customLiteLLMBaseUrl();
      await http.registerProviderKey({
        tenantId: tenantId.trim(),
        provider,
        apiKey,
        ...(nextBaseUrl ? { baseUrl: nextBaseUrl } : {}),
        ...(nextLiteLLMBaseUrl ? { litellmBaseUrl: nextLiteLLMBaseUrl } : {}),
      });
      setApiKey('');
      await onRegistered();
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setBusy(false);
    }
  }

  /** Probes the stored key, or unsaved form values when inline is supplied. */
  async function testConnection(
    targetProvider: PlatformProviderKeyProvider,
    inline?: { apiKey: string; baseUrl?: string }
  ) {
    if (!tenantId.trim()) return;
    setTestingProvider(targetProvider);
    try {
      const result = await http.testProviderKey({
        tenantId: tenantId.trim(),
        provider: targetProvider,
        ...(inline?.apiKey.trim() ? { apiKey: inline.apiKey.trim() } : {}),
        ...(inline?.baseUrl?.trim() ? { baseUrl: inline.baseUrl.trim() } : {}),
      });
      onToast(
        result.ok
          ? {
              tone: 'success',
              // Prefixed with the provider id: a probe settles after the admin
              // may already have switched to another provider tab, and the
              // panel shows only one provider at a time.
              message: `${targetProvider}: ${t('providerKeys.providerConnectionOk', {
                latency: result.latencyMs != null ? ` (${result.latencyMs}ms)` : '',
              })}`,
            }
          : {
              tone: 'error',
              message: `${targetProvider}: ${providerTestErrorMessage(result, 'provider', translate)}`,
            }
      );
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setTestingProvider(null);
    }
  }

  async function testLiteLLMConnection(
    targetProvider: PlatformProviderKeyProvider,
    nextBaseUrl?: string
  ) {
    if (!tenantId.trim()) return;
    setTestingLiteLLM(targetProvider);
    const trimmedBaseUrl = nextBaseUrl?.trim();
    try {
      const result = await http.testLiteLLMConnection({
        tenantId: tenantId.trim(),
        provider: targetProvider,
        ...(trimmedBaseUrl && !isSharedLiteLLMBaseUrlSnapshot(trimmedBaseUrl)
          ? { litellmBaseUrl: trimmedBaseUrl }
          : {}),
      });
      onToast(
        result.ok
          ? {
              tone: 'success',
              message: `${targetProvider}: ${t('providerKeys.litellmConnectionOk', {
                latency: result.latencyMs != null ? ` (${result.latencyMs}ms)` : '',
              })}`,
            }
          : {
              tone: 'error',
              message: `${targetProvider}: ${providerTestErrorMessage(result, 'litellm', translate)}`,
            }
      );
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setTestingLiteLLM(null);
    }
  }

  async function removeProvider(targetProvider: PlatformProviderKeyProvider) {
    if (!tenantId.trim()) return;
    if (!window.confirm(t('providerKeys.removeConfirm', { provider: targetProvider }))) return;
    setBusy(true);
    try {
      await http.removeProviderKey(tenantId.trim(), targetProvider);
      await onRegistered();
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setBusy(false);
    }
  }

  async function bulkDeployProviderModels(targetProvider: PlatformProviderKeyProvider) {
    if (!tenantId.trim()) return;
    const noDeployMessage =
      deployBuckets.blocked.length > 0
        ? t('providerKeys.bulkDeployAllBlocked', { count: deployBuckets.blocked.length })
        : t('providerKeys.bulkDeployNothing', { provider: targetProvider });
    if (deployBuckets.deployable.length === 0) {
      onToast({ tone: 'error', message: noDeployMessage });
      return;
    }
    setBulkDeployingProvider(targetProvider);
    try {
      const skipped = deployBuckets.alreadyActive.length;
      const deploying = deployBuckets.deployable.length;
      if (
        !window.confirm(
          t('providerKeys.bulkDeployConfirm', {
            provider: targetProvider,
            deploying,
            skipped,
            blockedNotice:
              deployBuckets.blocked.length > 0
                ? t('providerKeys.bulkDeployBlockedNotice', {
                    blocked: deployBuckets.blocked.length,
                    blockedNames: deployBuckets.blocked.join(', '),
                  })
                : '',
          })
        )
      ) {
        return;
      }
      const result = await http.bulkDeployPlatformProviderModels(tenantId.trim(), {
        provider: targetProvider,
        modelKeys: deployBuckets.deployable,
      });
      await onRegistered();
      const refreshedDefinitions = await fetchDefinitionsFor(targetProvider);
      setDefinitionPage((currentPage) =>
        currentPage.provider === targetProvider
          ? {
              provider: targetProvider,
              items: refreshedDefinitions.items,
              total: refreshedDefinitions.total,
            }
          : currentPage
      );
      onToast({
        tone: 'success',
        message: t('providerKeys.bulkDeployedToast', {
          provider: targetProvider,
          deployed: result.deployments.length,
          skipped: deployBuckets.alreadyActive.length + result.skipped.length,
          blocked: deployBuckets.blocked.length,
        }),
      });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setBulkDeployingProvider(null);
    }
  }

  const current = keys.find((item) => item.provider === provider);
  const configuredProviders = new Set(
    keys.filter((item) => item.hasCredential).map((item) => item.provider)
  );
  // Preset set UNION the server's rows: during deploy skew the service can
  // return a provider this bundle's presets do not know yet, and that stored
  // credential must stay visible, testable, removable, and counted.
  const tabProviders: PlatformProviderKeyProvider[] = [
    ...PROVIDERS,
    ...keys.map((item) => item.provider).filter((item) => !PROVIDERS.includes(item)),
  ];
  const missingProviders = tabProviders.filter((item) => !configuredProviders.has(item));
  // One shared state per action, but only the selected provider's panel is on
  // screen — overlapping requests would clobber each other's in-flight label,
  // so any in-flight action locks all of them.
  const actionInFlight =
    testingProvider !== null || testingLiteLLM !== null || bulkDeployingProvider !== null;
  // The server's stored-key probe branch reads the STORED base URL and ignores
  // an inline one, so testing with an empty key field after editing the URL
  // would report success for the endpoint that is NOT on screen.
  const baseUrlDiverged = baseUrl.trim() !== baseUrlsForProvider(provider, keys).providerBaseUrl;
  const definitionsLoaded = definitionPage.provider === provider;
  const definitions = definitionsLoaded ? definitionPage.items : [];
  const definitionTotal = definitionsLoaded ? definitionPage.total : 0;
  const deployedKeys = new Set(
    definitions
      .filter((definition) =>
        definition.deployments?.some(
          (deployment) =>
            deployment.tenantId === tenantId.trim() && deployment.status === 'active'
        )
      )
      .map((definition) => definition.modelKey)
  );
  const pendingDefinitions = definitions.filter(
    (definition) => !deployedKeys.has(definition.modelKey)
  );
  const pendingPreview = pendingDefinitions.slice(0, 8).map((definition) => definition.modelKey);
  const pendingMore = pendingDefinitions.length - pendingPreview.length;
  const pendingNames = [
    pendingPreview.join(', '),
    pendingMore > 0 ? t('providerKeys.deployPendingMore', { count: pendingMore }) : '',
  ]
    .filter(Boolean)
    .join(', ');
  const deployBuckets = bulkDeployBuckets(definitions, tenantId);

  return (
    <section className="content-stack">

      <div className="wide-navigation">
        <TabStrip level="nested" ariaLabel={t('providerKeys.providerTabs')}
          tabs={tabProviders.map((item) => ({ id: item, label: item }))}
          value={provider} onChange={changeProvider} />
      </div>
      <label className="compact-navigation">
        {t('providerKeys.providerTabs')}
        <select value={provider} onChange={(event) => changeProvider(event.target.value as PlatformProviderKeyProvider)}>
          {tabProviders.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </label>
      <section className="panel provider-key-panel">
        <header className="provider-key-summary">
          <div className="credential-head">
            <h2>{provider}</h2>
            <span className={current?.hasCredential ? 'badge active' : 'badge'}>
              {current?.hasCredential ? t('common.set') : t('common.empty')}
            </span>
          </div>
          {current?.hasCredential ? (
            <div className="credential-info">
              {current.maskedKey ? <small>{current.maskedKey}</small> : null}
              {current.baseUrl ? (
                <small>
                  {t('providerKeys.baseUrl')}: {current.baseUrl}
                </small>
              ) : null}
              {current.litellmBaseUrl ? <small>LiteLLM: {current.litellmBaseUrl}</small> : null}
              {current.updatedAt ? (
                <small>{new Date(current.updatedAt).toLocaleString()}</small>
              ) : null}
            </div>
          ) : null}
        </header>
        <div className="provider-key-workspace">
          <section className="provider-credentials" aria-labelledby="provider-credentials-title">
            <h3 id="provider-credentials-title">{t('providerKeys.credentialsTitle')}</h3>
            <form className="form-grid provider-credential-form" onSubmit={register}>
              <FormField
                label={t('providerKeys.apiKey')}
                type="password"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
              />
              <FormField
                label={t('providerKeys.baseUrl')}
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
                placeholder={defaultBaseUrlFor(provider)}
              />
              <FormField
                label={t('providerKeys.litellmBaseUrl')}
                value={litellmBaseUrl}
                onChange={(event) => setLiteLLMBaseUrl(event.target.value)}
                placeholder={DEFAULT_LITELLM_BASE_URL}
              />
              <div className="form-actions provider-connection-actions">
                <button disabled={!tenantId.trim() || !apiKey.trim() || busy}>
                  {t('providerKeys.register')}
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={
                    !tenantId.trim() ||
                    actionInFlight ||
                    (!apiKey.trim() && (!current?.hasCredential || baseUrlDiverged))
                  }
                  onClick={() =>
                    testConnection(provider, apiKey.trim() ? { apiKey, baseUrl } : undefined)
                  }
                >
                  {testingProvider === provider
                    ? t('providerKeys.testingProvider')
                    : t('providerKeys.testProvider')}
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={!tenantId.trim() || !litellmBaseUrl.trim() || actionInFlight}
                  onClick={() => testLiteLLMConnection(provider, litellmBaseUrl)}
                >
                  {testingLiteLLM === provider
                    ? t('providerKeys.testingLitellm')
                    : t('providerKeys.testLitellm')}
                </button>
              </div>
            </form>
            {current?.hasCredential ? (
              <div className="form-actions provider-remove-actions">
                <button
                  type="button"
                  className="secondary danger"
                  disabled={!tenantId.trim() || busy}
                  onClick={() => void removeProvider(provider)}
                >
                  {t('providerKeys.remove')}
                </button>
              </div>
            ) : null}
          </section>
          <aside className="provider-readiness" aria-labelledby="provider-readiness-title">
            <h3 id="provider-readiness-title">{t('providerKeys.readinessTitle')}</h3>
            {definitionsLoaded ? (
              <div className="credential-info provider-readiness-status">
                <p>
                  {t('providerKeys.deployStatus', {
                    total: definitionTotal,
                    deployed: deployedKeys.size,
                    pending: pendingDefinitions.length,
                  })}
                </p>
                {pendingNames ? (
                  <small>{t('providerKeys.deployPendingList', { names: pendingNames })}</small>
                ) : (
                  <small>{t('providerKeys.deployNothingPending')}</small>
                )}
                {definitionTotal > definitions.length ? (
                  <small>
                    {t('providerKeys.deployStatusTruncated', {
                      total: definitionTotal,
                      shown: definitions.length,
                    })}
                  </small>
                ) : null}
              </div>
            ) : null}
            {deployBuckets.blocked.length > 0 ? (
              <p className="screen-help provider-readiness-blocked">
                {t('providerKeys.bulkDeployBlockedNotice', {
                  blocked: deployBuckets.blocked.length,
                  blockedNames: deployBuckets.blocked.join(', '),
                })}
              </p>
            ) : null}
            {!current?.hasCredential ? <p className="screen-help">{t('providerKeys.noCredentialYet')}</p> : null}
            <div className="provider-readiness-actions">
              <button
                type="button"
                disabled={
                  !tenantId.trim() ||
                  busy ||
                  !current?.hasCredential ||
                  pendingDefinitions.length === 0 ||
                  actionInFlight
                }
                onClick={() => void bulkDeployProviderModels(provider)}
              >
                {bulkDeployingProvider === provider
                  ? t('providerKeys.bulkDeploying')
                  : t('providerKeys.bulkDeployModels', { count: deployBuckets.deployable.length })}
              </button>
              {onOpenManualProviderModels ? (
                <button type="button" className="secondary" onClick={onOpenManualProviderModels}>
                  {t('platform.manualProviderModels')}
                </button>
              ) : null}
              {onOpenAssignment ? (
                <div className="provider-assignment-action">
                  <p className="screen-help">{t('providerKeys.afterDeployHint')}</p>
                  <button type="button" className="secondary" onClick={onOpenAssignment}>
                    {t('manualProviderModels.openAssignment')}
                  </button>
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      </section>
      <details className="provider-registration-summary">
        <summary>{tenantId ? t('providerKeys.keyProgress', { configured: configuredProviders.size, total: tabProviders.length }) : t('platform.selectTenant')}</summary>
        <p>{t('providerKeys.optionalProviders')}</p>
        {missingProviders.length > 0 ? <p>{t('providerKeys.keyProgressMissing', { missing: missingProviders.join(', ') })}</p> : null}
      </details>
    </section>
  );
}
